import { 
  collection, 
  doc, 
  setDoc, 
  getDoc,
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';

export const uploadPhoto = async (userId, photoFile) => {
  try {
    const photoId = `${Date.now()}_${Math.random().toString(36).substring(7)}`;
    const storageRef = ref(storage, `users/${userId}/photos/${photoId}`);
    
    await uploadBytes(storageRef, photoFile);
    const downloadURL = await getDownloadURL(storageRef);
    
    return { success: true, photoURL: downloadURL, photoId };
  } catch (error) {
    console.error('Upload photo error:', error);
    return { success: false, error: error.message };
  }
};

export const uploadPhotoFromBase64 = async (userId, base64Data) => {
  try {
    const response = await fetch(base64Data);
    const blob = await response.blob();
    
    const photoId = `${Date.now()}_${Math.random().toString(36).substring(7)}`;
    const storageRef = ref(storage, `users/${userId}/photos/${photoId}`);
    
    await uploadBytes(storageRef, blob);
    const downloadURL = await getDownloadURL(storageRef);
    
    return { success: true, photoURL: downloadURL, photoId };
  } catch (error) {
    console.error('Upload photo from base64 error:', error);
    return { success: false, error: error.message };
  }
};

export const createEntry = async (babyBookId, entryData) => {
  try {
    const entryRef = doc(collection(db, 'entries'));
    const entryId = entryRef.id;
    
    const entry = {
      ...entryData,
      id: entryId,
      babyBookId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      deleted: false
    };
    
    await setDoc(entryRef, entry);
    
    // Update baby book photo count if photos were added
    if (entryData.photos && entryData.photos.length > 0) {
      const { updateBabyBook } = await import('./babyBookService');
      const babyBookRef = doc(db, 'babyBooks', babyBookId);
      const babyBookSnap = await getDoc(babyBookRef);
      if (babyBookSnap.exists()) {
        const currentCount = babyBookSnap.data().photoCount || 0;
        await updateBabyBook(babyBookId, {
          photoCount: currentCount + entryData.photos.length
        });
      }
    }
    return { success: true, entryId, entry };
  } catch (error) {
    console.error('Create entry error:', error);
    return { success: false, error: error.message };
  }
};

export const getBabyBookEntries = async (babyBookId) => {
  try {
    const entriesQuery = query(
      collection(db, 'entries'),
      where('babyBookId', '==', babyBookId),
      where('deleted', '==', false),
      orderBy('timestamp', 'asc')
    );
    
    const querySnapshot = await getDocs(entriesQuery);
    const entries = [];
    
    querySnapshot.forEach((doc) => {
      entries.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, entries };
  } catch (error) {
    console.error('Get entries error:', error);
    return { success: false, error: error.message };
  }
};

export const updateEntry = async (entryId, updates) => {
  try {
    await updateDoc(doc(db, 'entries', entryId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Update entry error:', error);
    return { success: false, error: error.message };
  }
};

export const deleteEntry = async (entryId, userId) => {
  try {
    await updateDoc(doc(db, 'entries', entryId), {
      deleted: true,
      deletedAt: serverTimestamp(),
      deletedBy: userId
    });
    
    return { success: true };
  } catch (error) {
    console.error('Delete entry error:', error);
    return { success: false, error: error.message };
  }
};

export const restoreEntry = async (entryId) => {
  try {
    await updateDoc(doc(db, 'entries', entryId), {
      deleted: false,
      deletedAt: null,
      deletedBy: null
    });
    
    return { success: true };
  } catch (error) {
    console.error('Restore entry error:', error);
    return { success: false, error: error.message };
  }
};

export const getDeletedEntries = async (babyBookId) => {
  try {
    const entriesQuery = query(
      collection(db, 'entries'),
      where('babyBookId', '==', babyBookId),
      where('deleted', '==', true),
      orderBy('deletedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(entriesQuery);
    const entries = [];
    
    querySnapshot.forEach((doc) => {
      entries.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, entries };
  } catch (error) {
    console.error('Get deleted entries error:', error);
    return { success: false, error: error.message };
  }
};

export const createMilestone = async (babyBookId, milestoneData) => {
  try {
    const milestoneRef = doc(collection(db, 'milestones'));
    const milestoneId = milestoneRef.id;
    
    const milestone = {
      ...milestoneData,
      id: milestoneId,
      babyBookId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(milestoneRef, milestone);
    
    return { success: true, milestoneId, milestone };
  } catch (error) {
    console.error('Create milestone error:', error);
    return { success: false, error: error.message };
  }
};

export const getBabyBookMilestones = async (babyBookId) => {
  try {
    const milestonesQuery = query(
      collection(db, 'milestones'),
      where('babyBookId', '==', babyBookId),
      orderBy('date', 'asc')
    );
    
    const querySnapshot = await getDocs(milestonesQuery);
    const milestones = [];
    
    querySnapshot.forEach((doc) => {
      milestones.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, milestones };
  } catch (error) {
    console.error('Get milestones error:', error);
    return { success: false, error: error.message };
  }
};

export const updateMilestone = async (milestoneId, updates) => {
  try {
    await updateDoc(doc(db, 'milestones', milestoneId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Update milestone error:', error);
    return { success: false, error: error.message };
  }
};

export const deleteMilestone = async (milestoneId) => {
  try {
    await deleteDoc(doc(db, 'milestones', milestoneId));
    
    return { success: true };
  } catch (error) {
    console.error('Delete milestone error:', error);
    return { success: false, error: error.message };
  }
};